import React, { useState } from 'react';
import { Select, Avatar, Badge, Tooltip, Spin, Alert } from 'antd';
import { 
  TeamOutlined, 
  EnvironmentOutlined, 
  GlobalOutlined,
  ApartmentOutlined,
  CrownOutlined,
  ToolOutlined 
} from '@ant-design/icons';
import { useMultiCmt } from '../../contexts/MultiCmtContext';
import './CMTSelector.css';

const { Option } = Select;

/**
 * 🎯 CMT Selector Component
 * 
 * Allows users to switch between multiple Crisis Management Teams
 * they have access to within their organization.
 * 
 * Features:
 * - Visual indicator of active CMT
 * - Role display for each CMT
 * - Default CMT highlighting
 * - Responsive design
 * - Loading states
 * 
 * @author Crisis Compass Development Team
 * @version 1.0.0 - Phase 1 Multi-CMT Implementation
 */

const CMTSelector = ({ 
  size = 'default',
  style = {},
  placement = 'bottomLeft',
  disabled = false,
  showRoles = true,
  showMetadata = true
}) => {
  const {
    activeCmt,
    availableCmts,
    defaultCmt,
    switchCMT,
    getUserRoleInCMT,
    loading,
    error,
    isMultiCmtEnabled
  } = useMultiCmt();

  const [switching, setSwitching] = useState(false);

  /**
   * 🔄 Handle CMT switch with loading state
   */
  const handleCMTSwitch = async (cmtId) => {
    if (cmtId === activeCmt?.id) return;

    try {
      setSwitching(true);
      await switchCMT(cmtId);
    } catch (error) {
      console.error('Failed to switch CMT:', error);
      // Error is handled by context
    } finally {
      setSwitching(false);
    }
  };



  /**
   * 🎨 Get appropriate icon for CMT type (Enhanced for Hierarchy)
   */
  const getCMTTypeIcon = (type, isGlobalCmt = false, level = 0) => {
    // 🌍 Global CMT gets special icon
    if (isGlobalCmt || type?.toLowerCase() === 'global') {
      return <GlobalOutlined />;
    }
    
    switch (type?.toLowerCase()) {
      case 'regional': return <EnvironmentOutlined />;
      case 'functional': return <ToolOutlined />;
      case 'division': return <ApartmentOutlined />;
      case 'primary': return <CrownOutlined />;
      default: return <TeamOutlined />;
    }
  };

  /**
   * 🎨 Get appropriate color for CMT type (Enhanced for Hierarchy)
   */
  const getCMTTypeColor = (type, isGlobalCmt = false, level = 0) => {
    // 🌍 Global CMT gets special color
    if (isGlobalCmt || type?.toLowerCase() === 'global') {
      return '#ff4d4f'; // Red for Global CMT
    }
    
    switch (type?.toLowerCase()) {
      case 'regional': return '#52c41a';
      case 'functional': return '#1890ff';
      case 'division': return '#fa8c16';
      case 'primary': return '#faad14';
      default: return '#722ed1';
    }
  };

  /**
   * 🏗️ Get hierarchy level indicator
   */
  const getHierarchyIndicator = (cmt) => {
    if (cmt.isGlobalCmt) {
      return '🌍'; // Global CMT
    } else if (cmt.parentCmtId) {
      return '📍'; // Child CMT
    }
    return null;
  };

  /**
   * 🎨 Format user role for display
   */
  const formatRole = (role) => {
    if (!role) return '';
    return role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Don't render if multi-CMT is not enabled (single CMT or no CMTs)
  if (!isMultiCmtEnabled) {
    return null;
  }

  // Loading state
  if (loading) {
    return (
      <div className="cmt-selector-loading" style={style}>
        <Spin size="small" />
        <span style={{ marginLeft: 8 }}>Loading CMTs...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="cmt-selector-error" style={style}>
        <Alert 
          message="CMT Loading Error" 
          description={error}
          type="error" 
          size="small"
          showIcon
        />
      </div>
    );
  }

  // No CMTs available
  if (availableCmts.length === 0) {
    return (
      <div className="cmt-selector-empty" style={style}>
        <Alert 
          message="No CMTs Available" 
          description="You are not assigned to any Crisis Management Teams."
          type="warning" 
          size="small"
          showIcon
        />
      </div>
    );
  }

  return (
    <div className="cmt-selector-container" style={style}>
      <Select
        value={activeCmt?.id}
        onChange={handleCMTSwitch}
        size={size}
        style={{ minWidth: 200, ...style }}
        placeholder="Select Crisis Management Team"
        loading={switching}
        disabled={disabled || switching}
        placement={placement}
        optionLabelProp="label"
        className="cmt-selector"
      >
        {availableCmts.map((cmt) => {
          const isDefault = defaultCmt?.id === cmt.id;
          const userRole = getUserRoleInCMT(cmt.id);
          const typeColor = getCMTTypeColor(cmt.type, cmt.isGlobalCmt, cmt.level);
          const hierarchyIndicator = getHierarchyIndicator(cmt);
          
          return (
            <Option 
              key={cmt.id} 
              value={cmt.id}
              label={
                <div className="cmt-selector-label">
                  <Avatar 
                    size="small" 
                    style={{ 
                      backgroundColor: typeColor,
                      marginRight: 8 
                    }}
                    icon={getCMTTypeIcon(cmt.type, cmt.isGlobalCmt, cmt.level)}
                  />
                  <span className="cmt-name">
                    {hierarchyIndicator && (
                      <span style={{ marginRight: 4, fontSize: '12px' }}>
                        {hierarchyIndicator}
                      </span>
                    )}
                    {cmt.name}
                    {cmt.isGlobalCmt && (
                      <Badge 
                        count="GLOBAL" 
                        style={{ 
                          backgroundColor: '#ff4d4f',
                          marginLeft: 8,
                          fontSize: '10px'
                        }} 
                      />
                    )}
                    {isDefault && !cmt.isGlobalCmt && (
                      <Badge 
                        count="DEFAULT" 
                        style={{ 
                          backgroundColor: '#faad14',
                          marginLeft: 8,
                          fontSize: '10px'
                        }} 
                      />
                    )}
                  </span>
                </div>
              }
            >
              <div className="cmt-option">
                <div className="cmt-option-header">
                  <div className="cmt-option-left">
                    <Avatar 
                      size="small" 
                      style={{ backgroundColor: typeColor }}
                      icon={getCMTTypeIcon(cmt.type, cmt.isGlobalCmt, cmt.level)}
                    />
                    <div className="cmt-option-info">
                      <div className="cmt-option-name">
                        {cmt.name}
                        {isDefault && (
                          <Badge 
                            count="DEFAULT" 
                            style={{ 
                              backgroundColor: '#faad14',
                              marginLeft: 8,
                              fontSize: '10px'
                            }} 
                          />
                        )}
                      </div>
                      {showRoles && userRole && (
                        <div className="cmt-option-role">
                          Role: {formatRole(userRole)}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {activeCmt?.id === cmt.id && (
                    <div className="cmt-option-active">
                      <Badge status="processing" text="Active" />
                    </div>
                  )}
                </div>
                
                {cmt.description && (
                  <div className="cmt-option-description">
                    {cmt.description}
                  </div>
                )}
                
                {showMetadata && cmt.metadata && (
                  <div className="cmt-option-metadata">
                    {cmt.metadata.region && (
                      <span className="metadata-item">
                        📍 {cmt.metadata.region}
                      </span>
                    )}
                    {cmt.metadata.businessUnit && (
                      <span className="metadata-item">
                        🏢 {cmt.metadata.businessUnit}
                      </span>
                    )}
                    {cmt.members && (
                      <span className="metadata-item">
                        👥 {cmt.members.filter(m => m.isActive).length} members
                      </span>
                    )}
                  </div>
                )}
              </div>
            </Option>
          );
        })}
      </Select>
      
      {activeCmt && (
        <Tooltip title={`Active CMT: ${activeCmt.name} (${formatRole(getUserRoleInCMT(activeCmt.id))})`}>
          <div className="cmt-selector-indicator">
            <Badge 
              status="processing" 
              style={{ 
                color: getCMTTypeColor(activeCmt.type)
              }}
            />
          </div>
        </Tooltip>
      )}
    </div>
  );
};

export default CMTSelector;